package org.example.project.registrierung

import org.example.project.login.UnifiedLoginContent

import androidx.compose.runtime.Composable
import org.example.project.service.PasswordPolicy
import org.example.project.settings.PasswordRequirementHint
import org.example.project.utils.LocalizationManager
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLInputElement


class FahrerUiSlots(
    /** Einzeiliges Textfeld (text / email / tel / password). */
    val textInput: @Composable (
        type: InputType<String>,
        placeholder: String,
        value: String,
        error: Boolean,
        onInput: (String) -> Unit,
        onEnter: (() -> Unit)?,
        layout: (AttrsScope<HTMLInputElement>.() -> Unit)?
    ) -> Unit,

    /** Primärer Action-Button (Weiter / Anmelden / Registrierung abschließen). */
    val primaryButton: @Composable (
        label: String,
        enabled: Boolean,
        loading: Boolean,
        onClick: () -> Unit
    ) -> Unit,

    /** Horizontale Flex-Zeile für nebeneinander stehende Felder. */
    val inlineRow: @Composable (content: @Composable () -> Unit) -> Unit,

    /** Umschließt alle Formular-Felder eines Schritts. */
    val formContainer: @Composable (content: @Composable () -> Unit) -> Unit,

    /** Schritt-Überschrift und Untertitel oberhalb des Formulars. */
    val sectionTitle: @Composable (title: String, subtitle: String) -> Unit,

    /** Fehlertext auf Feld- oder Schritt-Ebene. */
    val errorText: @Composable (msg: String) -> Unit,

    /** Zurück-Pfeil (sichtbar ab Schritt 2). */
    val backArrow: @Composable (onClick: () -> Unit) -> Unit,

    /** Sprach-/Rollen-Platzhalter in Schritt 1 (visuell layout-spezifisch). */
    val languagePlaceholder: @Composable (selectedLanguage: String, onLanguage: (String) -> Unit) -> Unit,
)



typealias FahrerLoginFlowState = All
typealias FahrerRegFlowState = All

fun FahrerLoginFlowState(
    email: String = "",
    pw: String = "",
    emailErr: String? = null,
    pwErr: String? = null,
    loading: Boolean = false,
): All = All(
    email = email,
    passwort = pw,
    emailFehler = emailErr.orEmpty(),
    passwortFehler = pwErr.orEmpty(),
    loading = loading
)

fun FahrerRegFlowState(
    step: Int = 1,
    registrationMode: String = "",
    snapKey: String = "",
    snapKeyConnected: Boolean = false,
    firma: String = "",
    strasse: String = "",
    hausnummer: String = "",
    plz: String = "",
    ort: String = "",
    vorname: String = "",
    nachname: String = "",
    email: String = "",
    mobil: String = "",
    passwort: String = "",
    passwortWdh: String = "",
    loading: Boolean = false,
    error: String? = null,
    snapKeyError: String? = null,
    snapKeyLoading: Boolean = false,
    driverLanguage: String = "de",
): All = All(
    lfdNr = step,
    snapKey = snapKey,
    firma = firma,
    strasse = strasse,
    hausnummer = hausnummer,
    plz = plz,
    ort = ort,
    vorname = vorname,
    nachname = nachname,
    email = email,
    mobil = mobil,
    signatur = passwortWdh,
    passwort = passwort,
    loading = loading,
    registrationMode = registrationMode,
    snapKeyConnected = snapKeyConnected,
    snapKeyError = snapKeyError.orEmpty(),
    snapKeyLoading = snapKeyLoading,
    sprache = driverLanguage.ifBlank { "de" },
    bemerkung = error.orEmpty()
)

val FahrerLoginFlowState.pw: String get() = passwort
val FahrerLoginFlowState.emailErr: String? get() = emailFehler.takeIf { it.isNotBlank() }
val FahrerLoginFlowState.pwErr: String? get() = passwortFehler.takeIf { it.isNotBlank() }
val FahrerLoginFlowState.loginLoading: Boolean get() = loading

val FahrerRegFlowState.step: Int get() = lfdNr
val FahrerRegFlowState.passwortWdh: String get() = signatur
val FahrerRegFlowState.regLoading: Boolean get() = loading
val FahrerRegFlowState.regError: String? get() = bemerkung.takeIf { it.isNotBlank() }
val FahrerRegFlowState.driverLanguage: String get() = sprache.ifBlank { "de" }

class FahrerLoginCallbacks(
    val onEmail: (String) -> Unit,
    val onPw: (String) -> Unit,
    val onSubmit: () -> Unit,
    val onRegister: () -> Unit,
    val onForgotPassword: () -> Unit,
)

class FahrerRegCallbacks(
    val onStep: (Int) -> Unit,
    val onSnapKey: (String) -> Unit,
    val onConfirmSnapKey: () -> Unit,
    val onFirma: (String) -> Unit,
    val onStrasse: (String) -> Unit,
    val onHausnummer: (String) -> Unit,
    val onPlz: (String) -> Unit,
    val onOrt: (String) -> Unit,
    val onVorname: (String) -> Unit,
    val onNachname: (String) -> Unit,
    val onEmail: (String) -> Unit,
    val onMobil: (String) -> Unit,
    val onPasswort: (String) -> Unit,
    val onPasswortWdh: (String) -> Unit,
    val onLanguage: (String) -> Unit,
    val onFinalize: () -> Unit,
)

@Composable
fun FahrerLoginFlow(
    state: FahrerLoginFlowState,
    callbacks: FahrerLoginCallbacks,
    slots: FahrerUiSlots,
    onRegister: (() -> Unit)? = null,
) {
    val t: (String) -> String = { LocalizationManager.get(it) }
    val emailErr = state.emailErr
    val pwErr = state.pwErr
    UnifiedLoginContent(
        identifier = state.email,
        password = state.pw,
        identifierPlaceholder = t("driver_email"),
        loading = state.loginLoading,
        errorText = emailErr ?: pwErr,
        forgotLabel = t("driver_password_forgotten"),
        switchPrefix = if (onRegister != null) "Neu hier?" else null,
        switchLabel = if (onRegister != null) "Registrieren" else null,
        onIdentifierChange = callbacks.onEmail,
        onPasswordChange = callbacks.onPw,
        onSubmit = callbacks.onSubmit,
        onForgot = callbacks.onForgotPassword,
        onSwitch = onRegister
    )
}

@Composable
fun FahrerRegFlow(
    state: FahrerRegFlowState,
    callbacks: FahrerRegCallbacks,
    slots: FahrerUiSlots,
    desktopLayout: Boolean = false,
    onLogin: () -> Unit,
) {
    val t: (String) -> String = { LocalizationManager.get(it) }
    val error = state.regError
    val snapKeyError = state.snapKeyError.takeIf { it.isNotBlank() }
    val snapKeyLoading = state.snapKeyLoading
    val companyReadOnly = state.registrationMode == "snapkey" && state.snapKeyConnected
    if (state.step > 1) {
        slots.backArrow {
            when (state.step) {
                4 -> callbacks.onStep(if (state.registrationMode == "snapkey") 2 else 3)
                else -> callbacks.onStep(state.step - 1)
            }
        }
    }

    slots.sectionTitle(
        when (state.step) {
            1    -> t("driver_registration_step_language_title")
            2    -> t("driver_registration_step_snapkey_title")
            3    -> t("driver_registration_step_company_title")
            else -> t("driver_registration_step_access_title")
        },
        when (state.step) {
            1    -> t("driver_registration_step_language_subtitle")
            2    -> t("driver_registration_step_snapkey_subtitle")
            3    -> t("driver_registration_step_company_subtitle")
            else -> t("driver_registration_step_access_subtitle")
        },
    )

    slots.formContainer {
        when (state.step) {

            1 -> {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        flex(1)
                        width(100.percent)
                        minHeight(0.px)
                    }
                }) {
                    Div({
                        style {
                            flex(1)
                            minHeight(0.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                        }
                    }) {
                        slots.languagePlaceholder(state.driverLanguage, callbacks.onLanguage)
                    }
                    Div({
                        style {
                            property("margin-top", "auto")
                            width(100.percent)
                        }
                    }) {
                        slots.primaryButton(t("weiter"), true, false) { callbacks.onStep(2) }
                    }
                    FahrerRegistrationLoginSwitch(
                        onLogin = onLogin,
                        desktopLayout = desktopLayout
                    )
                }
            }

            2 -> {
                slots.textInput(InputType.Text, t("driver_snapkey"), state.snapKey, snapKeyError != null,
                    callbacks.onSnapKey, callbacks.onConfirmSnapKey, null)
                snapKeyError?.let { slots.errorText(it) }
                error?.let { slots.errorText(it) }
                slots.primaryButton(
                    if (snapKeyLoading) "..." else t("driver_registration_check_snapkey"),
                    state.snapKey.isNotBlank() && !snapKeyLoading,
                    snapKeyLoading,
                    callbacks.onConfirmSnapKey
                )
            }

            3 -> {
                slots.textInput(InputType.Text, t("driver_company"), state.firma, false,
                    if (companyReadOnly) ({}) else callbacks.onFirma, null
                ) { if (companyReadOnly) disabled() }
                slots.inlineRow {
                    slots.textInput(InputType.Text, t("driver_street"), state.strasse, false,
                        if (companyReadOnly) ({}) else callbacks.onStrasse, null) {
                        if (companyReadOnly) disabled()
                        style { flex(1) }
                    }
                    slots.textInput(InputType.Text, t("driver_house_number"), state.hausnummer, false,
                        if (companyReadOnly) ({}) else callbacks.onHausnummer, null) {
                        if (companyReadOnly) disabled()
                        style { width(80.px) }
                    }
                }
                slots.inlineRow {
                    slots.textInput(InputType.Text, t("driver_postal_code"), state.plz, false,
                        if (companyReadOnly) ({}) else callbacks.onPlz, null) {
                        if (companyReadOnly) disabled()
                        style { width(120.px) }
                    }
                    slots.textInput(InputType.Text, t("driver_city"), state.ort, false,
                        if (companyReadOnly) ({}) else callbacks.onOrt, null) {
                        if (companyReadOnly) disabled()
                        style { flex(1) }
                    }
                }
                error?.let { slots.errorText(it) }
                val ok = state.firma.isNotBlank() && state.strasse.isNotBlank() &&
                    state.plz.isNotBlank() && state.ort.isNotBlank()
                slots.primaryButton(t("weiter"), ok, false) { callbacks.onStep(4) }
            }

            4 -> {
                slots.inlineRow {
                    slots.textInput(InputType.Text, t("driver_first_name"), state.vorname, false,
                        callbacks.onVorname, null) { style { flex(1) } }
                    slots.textInput(InputType.Text, t("driver_last_name"), state.nachname, false,
                        callbacks.onNachname, null) { style { flex(1) } }
                }
                slots.textInput(InputType.Email, t("driver_email"), state.email, false,
                    callbacks.onEmail, null, null)
                slots.textInput(InputType.Tel, t("driver_mobile"), state.mobil, false,
                    callbacks.onMobil, null, null)
                slots.inlineRow {
                    slots.textInput(InputType.Password, t("driver_password"), state.passwort, false,
                        callbacks.onPasswort, null) { style { flex(1) } }
                    val mismatch = state.passwortWdh.isNotEmpty() && state.passwort != state.passwortWdh
                    slots.textInput(InputType.Password, t("driver_password_repeat"), state.passwortWdh, mismatch,
                        callbacks.onPasswortWdh, null) { style { flex(1) } }
                }
                PasswordRequirementHint(state.passwort, t("driver_password_requirement"))
                if (state.passwortWdh.isNotEmpty() && state.passwort != state.passwortWdh)
                    slots.errorText(t("driver_password_mismatch"))
                error?.let { slots.errorText(it) }

                val ok = state.vorname.isNotBlank() && state.nachname.isNotBlank() &&
                    state.email.isNotBlank() && PasswordPolicy.isValid(state.passwort) &&
                    state.passwort == state.passwortWdh && !state.regLoading
                slots.primaryButton(
                    if (state.regLoading) "..." else t("driver_registration_finish"),
                    ok,
                    state.regLoading,
                    callbacks.onFinalize,
                )
            }
        }
    }
}
